<div class="smart-cull-page">
  <div class="smart-cull-header">
    <div class="header-left">
      <h1>Smart Cull Users</h1>
      <p>
        Showing page {{ pageNumber }} of {{ totalPages || 1 }} 
        (Total {{ totalCount }} users)
      </p>
    </div>

    <div class="header-right">
      <div class="search-box">
        <span class="search-icon">⌕</span>
        <input 
          type="text" 
          [value]="searchTerm" 
          (input)="onSearchChange($event)" 
          placeholder="Search users..." 
        />
      </div>

      <button type="button" class="create-btn" (click)="onCreateUser()">
        Create
      </button>
    </div>
  </div>

  @if (isLoading) {
    <div class="empty-state">Loading smart cull users...</div>
  } @else if (errorMessage && !isModalOpen) {
    <div class="empty-state error">{{ errorMessage }}</div>
  } @else {
    <div class="smart-cull-table-card">
      <div class="table-scroll">
        <table class="smart-cull-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>FIRST NAME</th>
              <th>LAST NAME</th>
              <th>PHONE NUMBER</th>
              <th>EMAIL (USERNAME)</th>
              <th>ACTIVE</th>
              <th>COUNTRY</th>
              <th>ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            @for (user of users; track user.smartCullUserId) {
              <tr>
                <td>{{ user.smartCullUserId }}</td>
                <td>{{ user.firstName }}</td>
                <td>{{ user.lastName }}</td>
                <td>{{ user.phoneNumber }}</td>
                <td class="truncate-cell">
                  {{ user.email }}<br>
                  <small class="text-muted">{{ user.username }}</small>
                </td>
                <td>
                  <span class="status-badge" [class.active]="user.isActive">
                    {{ user.isActive ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td>{{ user.country }}</td>
                <td>
                  <div class="action-buttons">
                    <button
                      type="button"
                      class="icon-action reset"
                      (click)="onResetPassword(user)"
                      [disabled]="isResettingUserId === user.smartCullUserId"
                      title="Reset Password"
                    >
                      <img src="/icons/reset.svg" alt="Reset Password" class="action-icon" />
                    </button>

                    <button
                      type="button"
                      class="icon-action edit"
                      (click)="openEditModal(user)"
                      title="Edit"
                    >
                      <img src="/icons/edit.svg" alt="Edit" class="action-icon" />
                    </button>

                    <button
                      type="button"
                      class="icon-action delete"
                      (click)="onDeleteUser(user)"
                      [disabled]="deletingUserId === user.smartCullUserId"
                      title="Delete"
                    >
                      <img src="/icons/delete.svg" alt="Delete" class="action-icon" />
                    </button>
                  </div>
                </td>
              </tr>
            }
            @if (users.length === 0) {
              <tr>
                <td colspan="8" class="text-center py-4">No users found.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- Pagination Controls -->
      <div class="pagination-controls" style="display: flex; justify-content: space-between; align-items: center; padding: 1rem; border-top: 1px solid var(--border-color);">
        <button 
          type="button" 
          class="btn-pagination" 
          style="padding: 0.5rem 1rem; border: 1px solid var(--border-color); border-radius: 4px; background: transparent; cursor: pointer;"
          (click)="previousPage()" 
          [disabled]="pageNumber === 1 || isLoading">
          Previous
        </button>
        <span class="page-info">Page {{ pageNumber }} of {{ totalPages || 1 }}</span>
        <button 
          type="button" 
          class="btn-pagination" 
          style="padding: 0.5rem 1rem; border: 1px solid var(--border-color); border-radius: 4px; background: transparent; cursor: pointer;"
          (click)="nextPage()" 
          [disabled]="pageNumber >= (totalPages || 1) || isLoading">
          Next
        </button>
      </div>

    </div>
  }

  @if (isModalOpen) {
    <div class="modal-overlay" (click)="closeModal()">
      <div class="smart-cull-modal" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <div>
            <h2>{{ isEditMode ? 'Edit Smart Cull User' : 'Create New Smart Cull User' }}</h2>
            <p>
              {{
                isEditMode
                  ? 'Update smart cull user information.'
                  : 'Add a new smart cull user to the system. Fill in all required fields.'
              }}
            </p>
          </div>
          <button type="button" class="close-btn" (click)="closeModal()">×</button>
        </div>

        <form [formGroup]="userForm" (ngSubmit)="saveUser()" class="modal-form">
          <div class="form-grid two-col">
            <div class="form-field">
              <label>First Name *</label>
              <input type="text" formControlName="firstName" />
              @if (firstNameControl?.touched && firstNameControl?.errors?.['required']) {
                <div class="field-error">First name is required.</div>
              }
            </div>

            <div class="form-field">
              <label>Last Name *</label>
              <input type="text" formControlName="lastName" />
              @if (lastNameControl?.touched && lastNameControl?.errors?.['required']) {
                <div class="field-error">Last name is required.</div>
              }
            </div>
          </div>

          <div class="form-grid two-col">
            <div class="form-field">
              <label>Email *</label>
              <input type="email" formControlName="email" />
              @if (emailControl?.touched && emailControl?.errors?.['required']) {
                <div class="field-error">Email is required.</div>
              }
              @if (emailControl?.touched && emailControl?.errors?.['email']) {
                <div class="field-error">Enter a valid email address.</div>
              }
            </div>

            <div class="form-field">
              <label>Username *</label>
              <input type="text" formControlName="username" />
              @if (usernameControl?.touched && usernameControl?.errors?.['required']) {
                <div class="field-error">Username is required.</div>
              }
            </div>
          </div>

          <div class="form-grid one-col">
            <div class="form-field">
              <label>Phone Number *</label>
              <input type="text" formControlName="phoneNumber" />
              @if (phoneNumberControl?.touched && phoneNumberControl?.errors?.['required']) {
                <div class="field-error">Phone number is required.</div>
              }
            </div>
          </div>

          <div class="form-grid two-col">
            <div class="form-field">
              <label>Status</label>
              <select formControlName="status">
                @for (status of statusOptions; track status) {
                  <option [value]="status">{{ status }}</option>
                }
              </select>
            </div>

            <div class="form-field">
              <label>Country *</label>
              <input type="text" formControlName="country" />
              @if (countryControl?.touched && countryControl?.errors?.['required']) {
                <div class="field-error">Country is required.</div>
              }
            </div>
          </div>

          <div class="form-grid two-col">
            <div class="form-field">
              <label>Address Line 1 *</label>
              <input type="text" formControlName="addressLine1" />
              @if (addressLine1Control?.touched && addressLine1Control?.errors?.['required']) {
                <div class="field-error">Address is required.</div>
              }
            </div>
            
            <div class="form-field">
              <label>Address Line 2 (Optional)</label>
              <input type="text" formControlName="addressLine2" />
            </div>
          </div>

          <div class="form-grid three-col">
            <div class="form-field">
              <label>City *</label>
              <input type="text" formControlName="city" />
              @if (cityControl?.touched && cityControl?.errors?.['required']) {
                <div class="field-error">City is required.</div>
              }
            </div>

            <div class="form-field">
              <label>State *</label>
              <input type="text" formControlName="state" />
              @if (stateControl?.touched && stateControl?.errors?.['required']) {
                <div class="field-error">State is required.</div>
              }
            </div>

            <div class="form-field">
              <label>Zip Code *</label>
              <input type="text" formControlName="zipCode" />
              @if (zipCodeControl?.touched && zipCodeControl?.errors?.['required']) {
                <div class="field-error">Zip Code is required.</div>
              }
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn-cancel" (click)="closeModal()">Cancel</button>
            <button type="submit" class="btn-save" [disabled]="isSaving">
              {{
                isSaving
                  ? isEditMode
                    ? 'Updating...'
                    : 'Creating...'
                  : isEditMode
                    ? 'Update User'
                    : 'Create User'
              }}
            </button>
          </div>
        </form>
      </div>
    </div>
  }
</div>

@if (isDeleteModalOpen()) {
  <div class="modal-overlay" (click)="closeDeleteModal()">
    <div class="delete-modal" (click)="$event.stopPropagation()">
      <div class="delete-modal-header">
        <h3>Delete Smart Cull User</h3>
        <button type="button" class="modal-close-btn" (click)="closeDeleteModal()">×</button>
      </div>

      <div class="delete-modal-body">
        <p>
          Are you sure you want to delete
          <strong>{{ deletingUser?.firstName }} {{ deletingUser?.lastName }}</strong>?
        </p>
      </div>

      <div class="delete-modal-actions">
        <button type="button" class="delete-cancel-btn" (click)="closeDeleteModal()">Cancel</button>
        <button
          type="button"
          class="delete-confirm-btn"
          (click)="confirmDeleteUser()"
          [disabled]="deletingUserId !== null"
        >
          {{ deletingUserId !== null ? 'Deleting...' : 'Delete' }}
        </button>
      </div>
    </div>
  </div>
}

@if (isResetModalOpen()) {
  <div class="modal-overlay" (click)="closeResetModal()">
    <div class="delete-modal" (click)="$event.stopPropagation()">
      <div class="delete-modal-header">
        <h3>Reset Password</h3>
        <button type="button" class="modal-close-btn" (click)="closeResetModal()">×</button>
      </div>

      <div class="delete-modal-body">
        <p>
          Are you sure you want to reset the password for
          <strong>{{ resettingUser?.firstName }} {{ resettingUser?.lastName }}</strong>?
        </p>
      </div>

      <div class="delete-modal-actions">
        <button type="button" class="delete-cancel-btn" (click)="closeResetModal()">Cancel</button>
        <button
          type="button"
          class="btn-save"
          style="padding: 0 1.25rem; font-weight: 600;"
          (click)="confirmResetPassword()"
          [disabled]="isResettingUserId !== null"
        >
          {{ isResettingUserId !== null ? 'Resetting...' : 'OK' }}
        </button>
      </div>
    </div>
  </div>
}
